import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ToolSidebar from "../../utils/ToolSideBar";
import ClientActionItems from "../dashboard/ClientActionItems";
import ClientTimeline from "../dashboard/ClientTimeline";
import "../../../css/client/ClientProjectDetail.css";
import { currentUserCanUseMeetings } from "../../utils/features";

const tabLabels = [
  ["overview", "Overview"],
  ["team", "Team"],
  ["client_actions", "Client Actions"],
  ["timeline", "Timeline"],
  ["files", "Files"],
  ["messages", "Messages"],
  ["meetings", "Meetings"],
  ["change_orders", "Change Orders"],
];

function capabilityTabs(capabilities) {
  if (!capabilities) return {};
  return capabilities.tabs || capabilities;
}

function EmptySection({ children }) {
  return <p className="empty-copy">{children}</p>;
}

export default function ClientProjectDetail({ project, loading, error, onRefresh }) {
  const [activeTab, setActiveTab] = useState("overview");
  const meetingsEnabled = currentUserCanUseMeetings();
  const capabilities = capabilityTabs(project?.capabilities);
  const canView = (tab) => capabilities[tab]?.view !== false;
  const tabs = tabLabels.filter(([tab]) => canView(tab) && (tab !== "meetings" || meetingsEnabled));

  useEffect(() => {
    if (tabs.length && !tabs.some(([tab]) => tab === activeTab)) {
      setActiveTab(tabs[0][0]);
    }
  }, [activeTab, tabs]);

  if (loading) return <div className="project-detail"><p>Loading project...</p></div>;

  const actionItems = project?.action_items || [];
  const milestones = project?.milestones || [];
  const progress = milestones.length ? Math.round((milestones.filter((item) => item.is_completed).length / milestones.length) * 100) : project?.progress_percentage || 0;
  const team = project?.team || {};

  return (
    <div className="project-detail-shell">
      <ToolSidebar active="projects" />
      <main className="project-detail">
        <Link to="/projects" className="back-link">Back to projects</Link>
        {error || !project ? <div className="card"><p>{error || "Project not found."}</p></div> : (
          <>
            <header className="project-detail-header">
              <span>{project.current_phase}</span>
              <h1>{project.project_name}</h1>
              <p>{project.summary}</p>
            </header>
            <nav className="project-tabs" aria-label="Project detail sections">
              {tabs.map(([tab, label]) => <button key={tab} type="button" className={activeTab === tab ? "active" : ""} onClick={() => setActiveTab(tab)}>{label}</button>)}
            </nav>

            {activeTab === "overview" && (
              <>
                <div className="detail-grid">
                  <div className="card"><h3>Status</h3><p>{project.status_label}</p></div>
                  <div className="card"><h3>Progress</h3><p>{progress}%</p></div>
                  <div className="card"><h3>Plan</h3><p>{project.selected_plan}</p></div>
                  <div className="card"><h3>Timeline</h3><p>{project.estimated_timeline}</p></div>
                  <div className="card"><h3>Project Manager</h3><p>{project.project_manager}</p></div>
                  <div className="card"><h3>Partner</h3><p>{project.referring_partner}</p></div>
                </div>
                <section className="card full"><h3>Scope</h3><p>{project.accepted_scope || project.description}</p></section>
                <section className="card full"><h3>Features</h3>{(project.included_features || []).concat(project.paid_addons || []).length === 0 ? <EmptySection>No feature list available yet.</EmptySection> : <ul>{(project.included_features || []).concat(project.paid_addons || []).map((item) => <li key={`${item.item_type}-${item.id}`}>{item.name}</li>)}</ul>}</section>
              </>
            )}

            {activeTab === "team" && (
              <section className="card full"><h3>Project Team</h3>{!team.project_manager && !(team.delivery_teams || []).some((item) => item.assigned_team) && !(team.qa_assignments || []).length ? <EmptySection>Your project team is being assembled.</EmptySection> : <div className="project-file-list">{team.project_manager && <span>Project Manager: {team.project_manager.name}</span>}{(team.delivery_teams || []).filter((item) => item.assigned_team).map((item) => <span key={item.discipline}>{item.label}: {item.assigned_team.name}</span>)}{(team.qa_assignments || []).map((item) => <span key={item.id}>Quality Assurance: {item.name}</span>)}</div>}</section>
            )}

            {activeTab === "client_actions" && <ClientActionItems items={actionItems} onUpdated={onRefresh} />}
            {activeTab === "timeline" && <ClientTimeline milestones={milestones} progress={progress} summary={{ milestone_count: milestones.length, milestone_progress_percentage: progress, current_milestone: milestones.find((item) => item.is_current), has_delayed_milestones: milestones.some((item) => item.is_delayed) }} />}

            {activeTab === "files" && (
              <section className="card full"><h3>Files</h3>{(project.files || []).length === 0 ? <EmptySection>No files yet.</EmptySection> : <div className="project-file-list">{(project.files || []).map((file) => <a key={file.id} href={file.download_url || file.url} target="_blank" rel="noreferrer">{file.title}</a>)}</div>}</section>
            )}

            {activeTab === "messages" && (
              <section className="card full"><h3>Messages</h3><p>Continue project conversations in your inbox.</p><Link to="/messages" className="primary-link">Open inbox</Link></section>
            )}

            {meetingsEnabled && activeTab === "meetings" && (
              <section className="card full"><h3>Meetings</h3>{(project.upcoming_meetings || []).length === 0 ? <EmptySection>No upcoming meetings for this project.</EmptySection> : <div className="meeting-list">{project.upcoming_meetings.map((meeting) => <Link key={meeting.id} to={`/meetings/${meeting.id}`}>{meeting.title || meeting.subject}<span>{new Date(meeting.start_at).toLocaleString()}</span></Link>)}</div>}<Link to="/meetings" className="primary-link">Manage meetings</Link></section>
            )}

            {activeTab === "change_orders" && (
              <section className="card full"><h3>Change Orders</h3>{(project.change_orders || []).length === 0 ? <EmptySection>No change orders for this project.</EmptySection> : <div className="project-file-list">{project.change_orders.map((item) => <span key={item.id}>{item.title} - {item.status_label}</span>)}</div>}</section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
